import {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Box, Button, Stack, Typography, Checkbox,
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, TablePagination,
    CircularProgress, Tooltip,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import UndoIcon from '@mui/icons-material/Undo';
import { sumByFsCode } from '@/services/accountService/glInvoiceService';
import TinyRowSearch from '@/pages/components/account/gl-invoice/mode/TinyRowSearch';
import { tinyRowMatchesQuery } from '@/pages/components/account/gl-invoice/mode/matchTinyRow';
import { sameSignOrNeutral } from '@/components/account/gl-invoice/mode/glTransferAlloc';

// ==================== 类型定义 ====================

/** 对方科目条目 */
export interface GlCateCounterpart {
    fsCode: string;
    fsName: string;
    columnTitle: string;
    amount: number;
    currency: string;
}

/** 明细行数据（兼容 GlSheetRowVO 和 InvoiceDetailWithCounterpartVO 两种来源） */
export interface GlCateDetailRow {
    invoiceId: string;
    date: string | null;
    description: string | null;
    voucherNo: string | null;
    invoiceNo: string | null;
    invoiceDate?: string | null;
    chequeNo?: string | null;
    details: string | null;
    /** GlTinyColumn 模式：通过複合 key 從 columnValues 取值 */
    columnValues?: Record<string, number>;
    /** Dialog 模式：直接传入 amount */
    amount?: number | null;
    /** 对方科目（可选，Dialog 模式下使用） */
    counterparts?: GlCateCounterpart[];
    /** 外币折算展示，如 "219.12 USD * 7.84"（仅外币银行账户） */
    fxConversion?: string | null;
}

export interface GlCateDetailProps {
    /** 列标题（英文名） */
    label: string;
    /** 列中文名（可选） */
    nameZh?: string;
    /** FS Code */
    fsCode: string;
    /** 当前列的汇总值（當期） */
    columnTotal: number;
    /** 上年列的汇总值 */
    priorYearTotal?: number;
    /** 筛选后的明细行数据 */
    rows: GlCateDetailRow[];
    /** 当前列複合 key，用于从 columnValues 中取值（GlTinyColumn 模式） */
    columnKey?: string;
    /** 是否多列 fsCode（如 BS-NCA-PPE），此時使用精確 key 匹配 */
    isMultiCol?: boolean;
    /** 是否正在加载 */
    loading?: boolean;
    /** 错误信息 */
    error?: string | null;
    /** 可选：按 fsCode 解析展示名称（audit WP 场景） */
    resolveFsLabel?: (fsCode: string) => string | null | undefined;
    /** Tiny Mode：点击发票行（排除 Prior Year b/f） */
    onRowClick?: (row: GlCateDetailRow, amount: number) => void;
    /** Tiny Mode：多选转移 */
    transferSelectable?: boolean;
    onTransferSelected?: (rows: Array<{row: GlCateDetailRow; amount: number}>) => void;
    /** Tiny Mode：多选原路返回 */
    onReturnSelected?: (rows: Array<{row: GlCateDetailRow; amount: number}>) => void;
    /** Tiny Mode 搜索：状态在父级，换 tab 保留关键字 */
    searchOpen?: boolean;
    searchQuery?: string;
    onSearchToggle?: () => void;
    onSearchQueryChange?: (value: string) => void;
}

// ==================== 工具函数 ====================

function formatNum(v: number | null | undefined) {
    if (v == null || v === 0) return '';
    if (v < 0) {
        return `(${Math.abs(v).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})})`;
    }
    return v.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

const formatAmount = (v: number | null | undefined) => {
    if (v == null) return '-';
    const num = Number(v);
    if (num < 0) {
        return `(${Math.abs(num).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})})`;
    }
    return num.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
};

interface JournalEntryItem {
    amount: number | null | undefined;
    label: string;
}

function resolveEntryLabel(
    fsCode: string,
    fsName?: string | null,
    columnTitle?: string | null,
    fallbackLabel?: string | null,
    resolveFsLabel?: (fsCode: string) => string | null | undefined,
): string {
    const resolved = String(
        fsName
        || columnTitle
        || (fallbackLabel && fallbackLabel !== fsCode ? fallbackLabel : '')
        || resolveFsLabel?.(fsCode)
        || fsCode
        || '',
    ).trim();
    return resolved || fsCode;
}

/** 对方科目（不含当前 FS 行，Amount 列已展示当前行） */
function buildCounterpartDisplayItems(
    row: GlCateDetailRow,
    resolveFsLabel?: (fsCode: string) => string | null | undefined,
): JournalEntryItem[] {
    return (row.counterparts ?? [])
        .map((cp) => ({
            amount: cp.amount,
            label: resolveEntryLabel(cp.fsCode, cp.fsName, cp.columnTitle, null, resolveFsLabel),
        }))
        .sort((a, b) => Math.abs(Number(b.amount) || 0) - Math.abs(Number(a.amount) || 0));
}

const ellipsisCellSx = {
    maxWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
} as const;

function EllipsisCell({value}: { value: string | null | undefined }) {
    const text = value || '';
    if (!text) return null;
    return (
        <Tooltip title={text} placement="top-start">
            <Box
                component="span"
                sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                }}
            >
                {text}
            </Box>
        </Tooltip>
    );
}

function JournalEntriesCell({
    entries,
    useCompactFormat,
}: {
    entries: JournalEntryItem[];
    useCompactFormat: boolean;
}) {
    if (entries.length === 0) {
        return null;
    }

    const formatValue = useCompactFormat ? formatNum : formatAmount;

    return (
        <Typography
            variant="body2"
            component="div"
            sx={{whiteSpace: 'nowrap', lineHeight: 1.4, fontSize: 12}}
        >
            {entries.map((entry, j) => (
                <Box key={j} component="span" sx={{mr: j < entries.length - 1 ? 1.5 : 0}}>
                    <Box
                        component="span"
                        sx={{fontWeight: 600, fontVariantNumeric: 'tabular-nums', mr: 0.5}}
                    >
                        {formatValue(entry.amount)}
                    </Box>
                    <Box component="span" sx={{color: 'text.secondary'}}>
                        {entry.label}
                    </Box>
                </Box>
            ))}
        </Typography>
    );
}

function formatRelatedEntriesText(
    entries: JournalEntryItem[],
    useCompactFormat: boolean,
): string {
    if (entries.length === 0) return '';
    const formatValue = useCompactFormat ? formatNum : formatAmount;
    return entries
        .map((entry) => `${formatValue(entry.amount)} ${entry.label}`.trim())
        .join(' ');
}

function escapeXml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function xmlTextCell(value: string | null | undefined): string {
    return `<Cell><Data ss:Type="String">${escapeXml(value ?? '')}</Data></Cell>`;
}

function xmlNumberCell(value: number | null | undefined): string {
    if (value == null || Number.isNaN(Number(value))) {
        return '<Cell><Data ss:Type="String"></Data></Cell>';
    }
    return `<Cell ss:StyleID="amt"><Data ss:Type="Number">${Number(value)}</Data></Cell>`;
}

function sanitizeFileName(name: string): string {
    const cleaned = name.replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_').trim();
    return cleaned || 'GL_Invoice_Detail';
}

function downloadGlCateDetailExcel(opts: {
    label: string;
    fsCode: string;
    columnTotal: number;
    priorYearTotal?: number;
    rows: GlCateDetailRow[];
    getRowAmount: (row: GlCateDetailRow) => number | null | undefined;
    showFxColumn: boolean;
    showRelatedEntriesColumn: boolean;
    useCompactFormat: boolean;
    resolveFsLabel?: (fsCode: string) => string | null | undefined;
}) {
    const {
        label,
        fsCode,
        columnTotal,
        priorYearTotal,
        rows,
        getRowAmount,
        showFxColumn,
        showRelatedEntriesColumn,
        useCompactFormat,
        resolveFsLabel,
    } = opts;

    const headers = [
        '#',
        'Date',
        'Description',
        'Voucher No.',
        'Invoice No.',
        'Details',
        ...(showFxColumn ? ['FX'] : []),
        'Amount',
        ...(showRelatedEntriesColumn ? ['Related Entries'] : []),
    ];

    const headerRow = `<Row>${headers.map((h) => xmlTextCell(h)).join('')}</Row>`;
    const dataRows = rows.map((row, idx) => {
        const amount = getRowAmount(row);
        const related = formatRelatedEntriesText(
            buildCounterpartDisplayItems(row, resolveFsLabel),
            useCompactFormat,
        );
        const cells = [
            xmlNumberCell(idx + 1),
            xmlTextCell(row.date),
            xmlTextCell(row.description),
            xmlTextCell(row.voucherNo),
            xmlTextCell(row.invoiceNo),
            xmlTextCell(row.details),
            ...(showFxColumn ? [xmlTextCell(row.fxConversion)] : []),
            xmlNumberCell(amount),
            ...(showRelatedEntriesColumn ? [xmlTextCell(related)] : []),
        ];
        return `<Row>${cells.join('')}</Row>`;
    }).join('');

    const summaryBits = [
        `Account: ${label} (${fsCode})`,
        `Rows: ${rows.length}`,
        `Current: ${formatNum(columnTotal) || '0'}`,
    ];
    if (priorYearTotal != null) {
        summaryBits.push(`Prior Year: ${formatNum(priorYearTotal) || '0'}`);
    }
    const summaryRow = `<Row>${xmlTextCell(summaryBits.join(' | '))}</Row>`;

    const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Styles>
  <Style ss:ID="amt"><NumberFormat ss:Format="#,##0.00;(#,##0.00)"/></Style>
 </Styles>
 <Worksheet ss:Name="GL Detail">
  <Table>
   ${summaryRow}
   <Row></Row>
   ${headerRow}
   ${dataRows}
  </Table>
 </Worksheet>
</Workbook>`;

    const blob = new Blob([xml], {type: 'application/vnd.ms-excel'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${sanitizeFileName(label)} - GL Detail.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// ==================== 组件 ====================

export default function GlCateDetail(props: GlCateDetailProps) {
    const {
        label,
        fsCode,
        columnTotal,
        priorYearTotal,
        rows,
        columnKey,
        isMultiCol,
        loading,
        error,
        resolveFsLabel,
        onRowClick,
        transferSelectable,
        onTransferSelected,
        onReturnSelected,
        searchOpen = false,
        searchQuery = '',
        onSearchToggle,
        onSearchQueryChange,
    } = props;

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
    const [selectError, setSelectError] = useState<string | null>(null);

    /** 获取行的金额值：多列 fsCode 用精確 key，單列 fsCode 用前綴匹配 */
    const getRowAmount = useCallback((row: GlCateDetailRow): number | null | undefined => {
        if (columnKey != null && row.columnValues) {
            if (isMultiCol) {
                const v = row.columnValues[columnKey];
                return v != null && v !== 0 ? v : undefined;
            }
            const sum = sumByFsCode(row.columnValues, fsCode);
            return sum !== 0 ? sum : undefined;
        }
        return row.amount;
    }, [columnKey, fsCode, isMultiCol]);

    const visibleRows = useMemo(() => {
        const q = searchQuery.trim();
        if (!q) return rows;
        return rows.filter((row) => tinyRowMatchesQuery({
            ...row,
            amount: row.amount ?? getRowAmount(row),
        }, q));
    }, [rows, searchQuery, getRowAmount]);

    // 切换账户 / 数据源 / 搜索时回到第一页；页码越界时收敛
    useEffect(() => {
        setPage(0);
        setSelectedIds(new Set());
        setSelectError(null);
    }, [fsCode, columnKey, rows[0]?.invoiceId, searchQuery]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(visibleRows.length / rowsPerPage) - 1);
        if (page > maxPage) setPage(maxPage);
    }, [visibleRows.length, rowsPerPage, page]);

    // 行变化时清理已不存在的勾选
    useEffect(() => {
        const valid = new Set(
            visibleRows
                .filter((r) => r.invoiceId && !String(r.invoiceId).startsWith('__prior_bf__'))
                .map((r) => r.invoiceId),
        );
        setSelectedIds((prev) => {
            const next = new Set<string>();
            for (const id of prev) {
                if (valid.has(id)) next.add(id);
            }
            return next.size === prev.size ? prev : next;
        });
    }, [visibleRows]);

    const pagedRows = useMemo(() => {
        const start = page * rowsPerPage;
        return visibleRows.slice(start, start + rowsPerPage);
    }, [visibleRows, page, rowsPerPage]);

    const showRelatedEntriesColumn = useMemo(
        () => visibleRows.some((row) => (row.counterparts?.length ?? 0) > 0),
        [visibleRows],
    );

    /** 有外币账户时 fxConversion 会设为 string（可为空串），用以强制显示 FX 列 */
    const showFxColumn = useMemo(
        () => visibleRows.some((row) => row.fxConversion !== undefined && row.fxConversion !== null),
        [visibleRows],
    );

    const isSelectableRow = useCallback((row: GlCateDetailRow) => {
        if (!transferSelectable) return false;
        if (!row.invoiceId || String(row.invoiceId).startsWith('__prior_bf__')) return false;
        if (String(row.description || '').trim().startsWith('Prior Year b/f')) return false;
        return true;
    }, [transferSelectable]);

    const selectableRows = useMemo(
        () => visibleRows.filter(isSelectableRow),
        [visibleRows, isSelectableRow],
    );

    const allSelectableChecked = selectableRows.length > 0
        && selectableRows.every((r) => selectedIds.has(r.invoiceId));
    const someSelectableChecked = selectableRows.some((r) => selectedIds.has(r.invoiceId));

    const toggleRow = useCallback((row: GlCateDetailRow, checked: boolean) => {
        if (!isSelectableRow(row)) return;
        const amt = Number(getRowAmount(row) ?? 0);
        setSelectError(null);
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (!checked) {
                next.delete(row.invoiceId);
                return next;
            }
            // 同符号校验
            let refSign = 0;
            for (const id of prev) {
                const other = visibleRows.find((r) => r.invoiceId === id);
                if (!other) continue;
                const oAmt = Number(getRowAmount(other) ?? 0);
                if (oAmt !== 0) {
                    refSign = Math.sign(oAmt);
                    break;
                }
            }
            if (refSign !== 0 && amt !== 0 && !sameSignOrNeutral(refSign, amt)) {
                setSelectError('Selected rows must have the same amount sign');
                return prev;
            }
            next.add(row.invoiceId);
            return next;
        });
    }, [getRowAmount, isSelectableRow, visibleRows]);

    const toggleAll = useCallback((checked: boolean) => {
        setSelectError(null);
        if (!checked) {
            setSelectedIds(new Set());
            return;
        }
        // 以第一条非零符号为基准，只勾同号
        let refSign = 0;
        for (const row of selectableRows) {
            const amt = Number(getRowAmount(row) ?? 0);
            if (amt !== 0) {
                refSign = Math.sign(amt);
                break;
            }
        }
        const next = new Set<string>();
        let skipped = false;
        for (const row of selectableRows) {
            const amt = Number(getRowAmount(row) ?? 0);
            if (refSign !== 0 && amt !== 0 && !sameSignOrNeutral(refSign, amt)) {
                skipped = true;
                continue;
            }
            next.add(row.invoiceId);
        }
        setSelectedIds(next);
        if (skipped) {
            setSelectError('Only same-sign rows were selected');
        }
    }, [getRowAmount, selectableRows]);

    const handleTransferSelected = useCallback(() => {
        if (!onTransferSelected || selectedIds.size === 0) return;
        const picked: Array<{row: GlCateDetailRow; amount: number}> = [];
        for (const row of visibleRows) {
            if (!selectedIds.has(row.invoiceId)) continue;
            picked.push({row, amount: Number(getRowAmount(row) ?? 0)});
        }
        if (picked.length === 0) return;
        onTransferSelected(picked);
    }, [getRowAmount, onTransferSelected, visibleRows, selectedIds]);

    const handleReturnSelected = useCallback(() => {
        if (!onReturnSelected || selectedIds.size === 0) return;
        const picked: Array<{row: GlCateDetailRow; amount: number}> = [];
        for (const row of visibleRows) {
            if (!selectedIds.has(row.invoiceId)) continue;
            picked.push({row, amount: Number(getRowAmount(row) ?? 0)});
        }
        if (picked.length === 0) return;
        onReturnSelected(picked);
    }, [getRowAmount, onReturnSelected, visibleRows, selectedIds]);

    const useCompactFormat = columnKey != null;

    const handleDownloadExcel = useCallback(() => {
        downloadGlCateDetailExcel({
            label,
            fsCode,
            columnTotal,
            priorYearTotal,
            rows: visibleRows,
            getRowAmount,
            showFxColumn,
            showRelatedEntriesColumn,
            useCompactFormat,
            resolveFsLabel,
        });
    }, [
        label,
        fsCode,
        columnTotal,
        priorYearTotal,
        visibleRows,
        getRowAmount,
        showFxColumn,
        showRelatedEntriesColumn,
        useCompactFormat,
        resolveFsLabel,
    ]);

    if (loading) {
        return (
            <Box sx={{textAlign: 'center', py: 4}}>
                <CircularProgress size={32}/>
                <Typography variant="body2" sx={{mt: 1}}>Loading...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Typography color="error" sx={{py: 2, px: 2}}>{error}</Typography>
        );
    }

    return (
        <Box sx={{p: 2}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 1.5}} spacing={1}>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{minWidth: 0, flex: 1}}>
                    <Box sx={{minWidth: 0}}>
                        <Typography variant="subtitle2" fontWeight={600}>
                            {label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ml: 1}}>
                            ({fsCode})
                        </Typography>
                    </Box>
                    {onSearchToggle && onSearchQueryChange && (
                        <TinyRowSearch
                            open={searchOpen}
                            query={searchQuery}
                            matchCount={searchQuery.trim() ? visibleRows.length : null}
                            onToggle={onSearchToggle}
                            onQueryChange={onSearchQueryChange}
                        />
                    )}
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{textAlign: 'right'}}>
                        <Typography variant="subtitle1" fontWeight={700}>
                            Current: {formatNum(columnTotal)}
                        </Typography>
                        {priorYearTotal != null && (
                            <Typography variant="caption" color="text.secondary">
                                Prior Year: {formatNum(priorYearTotal)}
                            </Typography>
                        )}
                    </Box>
                    {transferSelectable && selectedIds.size > 0 && (
                        <>
                            <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<UndoIcon/>}
                                onClick={handleReturnSelected}
                                disabled={!onReturnSelected}
                                sx={{textTransform: 'none', whiteSpace: 'nowrap'}}
                            >
                                Return ({selectedIds.size})
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<SwapHorizIcon/>}
                                onClick={handleTransferSelected}
                                sx={{textTransform: 'none', whiteSpace: 'nowrap'}}
                            >
                                Transfer Selected ({selectedIds.size})
                            </Button>
                        </>
                    )}
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FileDownloadIcon/>}
                        onClick={handleDownloadExcel}
                        disabled={visibleRows.length === 0}
                        sx={{textTransform: 'none', whiteSpace: 'nowrap'}}
                    >
                        Download Excel
                    </Button>
                </Stack>
            </Stack>

            {selectError && (
                <Typography variant="caption" color="error" sx={{display: 'block', mb: 1}}>
                    {selectError}
                </Typography>
            )}

            {rows.length === 0 && (
                <Typography sx={{py: 2, color: 'text.secondary', textAlign: 'center'}}>
                    No invoice records found for this FS code.
                </Typography>
            )}

            {rows.length > 0 && visibleRows.length === 0 && (
                <Typography sx={{py: 2, color: 'text.secondary', textAlign: 'center'}}>
                    No matching rows
                </Typography>
            )}

            {visibleRows.length > 0 && (
                <>
                    <TableContainer component={Paper} variant="outlined" sx={{maxHeight: 500, overflowX: 'hidden'}}>
                        <Table size="small" stickyHeader sx={{tableLayout: 'fixed', width: '100%'}}>
                            <TableHead>
                                <TableRow sx={{bgcolor: 'action.hover'}}>
                                    {transferSelectable && (
                                        <TableCell sx={{fontWeight: 600, width: 40, px: 0.5}} padding="checkbox">
                                            <Checkbox
                                                size="small"
                                                indeterminate={someSelectableChecked && !allSelectableChecked}
                                                checked={allSelectableChecked}
                                                onChange={(_, c) => toggleAll(c)}
                                                disabled={selectableRows.length === 0}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell sx={{fontWeight: 600, width: 36, px: 1}}>#</TableCell>
                                    <TableCell sx={{fontWeight: 600, width: 88, px: 1}}>Date</TableCell>
                                    <TableCell sx={{fontWeight: 600, width: '16%', px: 1}}>Description</TableCell>
                                    <TableCell sx={{fontWeight: 600, width: 72, px: 1}}>Voucher No.</TableCell>
                                    <TableCell sx={{fontWeight: 600, width: 96, px: 1}}>Invoice No.</TableCell>
                                    <TableCell sx={{fontWeight: 600, width: '14%', px: 1}}>Details</TableCell>
                                    {showFxColumn && (
                                        <TableCell sx={{fontWeight: 600, width: 140, px: 1, whiteSpace: 'nowrap'}}>
                                            FX
                                        </TableCell>
                                    )}
                                    <TableCell sx={{fontWeight: 600, width: 88, px: 1}} align="right">Amount</TableCell>
                                    {showRelatedEntriesColumn && (
                                        <TableCell sx={{fontWeight: 600, whiteSpace: 'nowrap', px: 1}}>Related Entries</TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pagedRows.map((row, idx) => {
                                    const amt = getRowAmount(row);
                                    const relatedEntries = buildCounterpartDisplayItems(row, resolveFsLabel);
                                    const isPriorBf = String(row.invoiceId || '').startsWith('__prior_bf__')
                                        || String(row.description || '').trim().startsWith('Prior Year b/f');
                                    const priorSx = isPriorBf
                                        ? {bgcolor: '#fff8e1', color: '#5d4037', fontStyle: 'italic'}
                                        : undefined;
                                    const rowNo = page * rowsPerPage + idx + 1;
                                    const clickable = !!onRowClick && !isPriorBf && !!row.invoiceId;
                                    const canSelect = isSelectableRow(row);
                                    const checked = canSelect && selectedIds.has(row.invoiceId);

                                    return (
                                        <TableRow
                                            key={row.invoiceId || rowNo}
                                            hover
                                            selected={checked}
                                            sx={{
                                                ...priorSx,
                                                ...(clickable ? {cursor: 'pointer'} : {}),
                                            }}
                                            onClick={clickable
                                                ? () => onRowClick?.(row, amt ?? 0)
                                                : undefined}
                                        >
                                            {transferSelectable && (
                                                <TableCell
                                                    padding="checkbox"
                                                    sx={{px: 0.5}}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <Checkbox
                                                        size="small"
                                                        checked={checked}
                                                        disabled={!canSelect}
                                                        onChange={(_, c) => toggleRow(row, c)}
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell sx={{px: 1}}>{rowNo}</TableCell>
                                            <TableCell sx={{whiteSpace: 'nowrap', px: 1}}>{row.date || ''}</TableCell>
                                            <TableCell sx={{...ellipsisCellSx, px: 1}}>
                                                <EllipsisCell value={row.description}/>
                                            </TableCell>
                                            <TableCell sx={{...ellipsisCellSx, px: 1}}>
                                                <EllipsisCell value={row.voucherNo}/>
                                            </TableCell>
                                            <TableCell sx={{...ellipsisCellSx, px: 1}}>
                                                <EllipsisCell value={row.invoiceNo}/>
                                            </TableCell>
                                            <TableCell sx={{...ellipsisCellSx, px: 1}}>
                                                <EllipsisCell value={row.details}/>
                                            </TableCell>
                                            {showFxColumn && (
                                                <TableCell sx={{...ellipsisCellSx, px: 1, whiteSpace: 'nowrap'}}>
                                                    <EllipsisCell value={row.fxConversion}/>
                                                </TableCell>
                                            )}
                                            <TableCell align="right" sx={{whiteSpace: 'nowrap', px: 1}}>
                                                {useCompactFormat ? formatNum(amt) : formatAmount(amt)}
                                            </TableCell>
                                            {showRelatedEntriesColumn && (
                                                <TableCell sx={{whiteSpace: 'nowrap', px: 1}}>
                                                    <JournalEntriesCell
                                                        entries={relatedEntries}
                                                        useCompactFormat={useCompactFormat}
                                                    />
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={visibleRows.length}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10) || 50);
                            setPage(0);
                        }}
                        rowsPerPageOptions={[20, 50, 100, 200]}
                        sx={{mt: 0.5, '.MuiTablePagination-toolbar': {minHeight: 40}}}
                    />

                    <Box sx={{textAlign: 'right', mt: 0.5}}>
                        <Typography variant="subtitle2" fontWeight={700}>
                            Rows: {visibleRows.length}{searchQuery.trim() ? ` / ${rows.length}` : ''} | Current: {formatNum(columnTotal)}
                            {priorYearTotal != null && ` | Prior Year: ${formatNum(priorYearTotal)}`}
                        </Typography>
                    </Box>
                </>
            )}
        </Box>
    );
}
